import { Helmet } from "react-helmet";
import "./assets/css/variables/colors/colors.css";
import "./assets/css/main.scss";
import { useEffect, useLayoutEffect, useState } from "react";
import useLocalStorage from "use-local-storage";
import Router from "router/Router";
import { ToastContainer } from "react-toastify";
import useWindowSize from "types/other/dimensions";
import RouterPc from "router/RouterPc";
import { createTheme, ThemeProvider } from "@mui/material";
import { getUserToken, onMessageListener } from "./firebase";
import Notification from "utils/other/Notification";
import md5 from "md5";
import * as Sentry from "@sentry/react";

import {
  useFcmDeviceMutation,
} from "redux/RTQ/IlenkradApi";
import DeviceRotate from "DeviceRotate";
import InstallModal from "utils/pwaModal/InstallModal";
import DeviceDetector from "device-detector-js";
import { CheckForPwaInstall } from "utils/pwaModal/CheckForPwaInstall";
import CustomInstallModal from "utils/pwaModal/CustomInstallModal";
import {
  getEnvironment,
  isProduction,
  isStaging,
} from "utils/other/Environment";
import { isJsonString } from "common/common";
import { getTheme } from "utils/v2/theme";
import { useBrandsConfig } from "utils/v2/BrandsConfig/BrandsConfigContext";
import useGTMTracking from "hooks/V2/useGTMTracking";

const App = () => {
  const { title, linkTags, metaTags } = useBrandsConfig();
  useGTMTracking();
  const authToken = localStorage.getItem("token");
  isProduction() &&
    Sentry.init({
      environment: getEnvironment(),
      dsn: "https://eaf999b0963debef15a42e1eff29c682@o523587.ingest.us.sentry.io/4507249262002176",
      integrations: [new Sentry.BrowserTracing()],
      tracesSampleRate: 0.01,
      release: process.env.REACT_APP_VERSION,
    });
  isStaging() &&
    Sentry.init({
      environment: getEnvironment(),
      dsn: "https://b8f478f7efdcb49b928d7b3305b4a07d@o523587.ingest.us.sentry.io/4507249309908992",
      integrations: [new Sentry.BrowserTracing()],
      tracesSampleRate: 0,
      release: process.env.REACT_APP_VERSION,
    });

  const getFcmAPK = () => {
    //@ts-ignore
    if (window?.ReactNativeWebView) {
      const obj = { code: "AUTH_TOKEN_FOUND" };
      const stringObj = JSON.stringify(obj);
      //@ts-ignore
      window?.ReactNativeWebView?.postMessage(stringObj);
    }
  };

  useEffect(() => {
    if (authToken) {
      getFcmAPK();
    }
  }, [authToken]);

  const [mutateFcm, { isSuccess }] = useFcmDeviceMutation();
  const [mutateFcmAPK, { isSuccess: isSuccessAPK }] = useFcmDeviceMutation();

  const [theme, setTheme] = useLocalStorage(
    "theme",
    getTheme() === "light" ? "light" : "dark",
  );

  const darkTheme = createTheme({
    palette: {
      mode: getTheme() === "light" ? "light" : "dark",
    },
  });

  const [isTokenFound, setTokenFound] = useState<any>(null);
  const [isTokenFoundAPK, setTokenFoundAPK] = useState<any>(null);

  const [show, setShow] = useState(false);
  const [notification, setNotification] = useState({ title: "", body: "" });

  useEffect(() => {
    getUserToken(setTokenFound);
  }, []);

  useEffect(() => {
    checkFcm();
  }, [isTokenFound]);

  const checkFcm = () => {
    if (localStorage.getItem("isFromAPK") === "true") return;

    const getNotif = localStorage.getItem("getAlerts")
      ? localStorage.getItem("getAlerts")
      : "true";
    if (getNotif === "false") {
      return;
    }
    const authToken = localStorage.getItem("token");
    const fcmToken = localStorage.getItem("fcmToken");
    if (authToken && isTokenFound) {
      if (fcmToken) {
        const fcmTokenObj = JSON.parse(fcmToken);
        if (fcmTokenObj.token === isTokenFound) {
          return;
        } else {
          mutateFcm({
            token: isTokenFound,
            platform: "web",
            token_md5: md5(isTokenFound),
          });
        }
      } else {
        mutateFcm({
          token: isTokenFound,
          platform: "web",
          token_md5: md5(isTokenFound),
        });
      }
    }
  };

  const checkAPKFcm = (isTokenFoundAPK: any) => {
    const getNotif = localStorage.getItem("getAlerts")
      ? localStorage.getItem("getAlerts")
      : "true";
    if (getNotif === "false") {
      return;
    }
    const authToken = localStorage.getItem("token");
    const fcmTokenAPK = localStorage.getItem("fcmTokenAPK");

    if (authToken && isTokenFoundAPK) {
      if (fcmTokenAPK) {
        const fcmTokenObj = JSON.parse(fcmTokenAPK);
        if (fcmTokenObj.token === isTokenFoundAPK) {
          return;
        } else {
          mutateFcmAPK({
            token: isTokenFoundAPK,
            platform: "app",
            token_md5: md5(isTokenFoundAPK),
          });
        }
      } else {
        mutateFcmAPK({
          token: isTokenFoundAPK,
          platform: "app",
          token_md5: md5(isTokenFoundAPK),
        });
      }
    }
  };

  useEffect(() => {
    if (isSuccess) {
      localStorage.setItem(
        "fcmToken",
        JSON.stringify({ token: isTokenFound, tokenMD5: md5(isTokenFound) }),
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccessAPK) {
      localStorage.setItem(
        "fcmTokenAPK",
        JSON.stringify({
          token: isTokenFoundAPK,
          tokenMD5: md5(isTokenFoundAPK),
        }),
      );
    }
  }, [isSuccessAPK]);

  onMessageListener()
    .then((payload) => {
      setShow(true);
      setNotification({
        title: payload.data.title,
        body: payload.data.body,
      });
    })
    .catch((err) => console.log("failed: ", err));

  const switchTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    window.location.reload();
  };
  const viewportWidth = useWindowSize();

  useLayoutEffect(() => {
    CheckForPwaInstall();
    const touchLogin = localStorage.getItem("touchLogin");

    if (touchLogin) {
      if (new Date().getTime() - new Date(touchLogin).getTime() >= 60000) {
        localStorage.setItem("shouldTouch", "true");
      } else {
        const now = new Date();
        localStorage.setItem("touchLogin", now.toISOString());
      }
    }
  }, []);

  const handleMessages = (event: any) => {
    if (event.data && isJsonString(event.data)) {
      const data = JSON.parse(event.data);

      switch (data.code) {
        case "APK":
          localStorage.setItem("isFromAPK", "true");
          break;

        case "FCM":
          setTokenFoundAPK(data.token);
          checkAPKFcm(data.token);
          break;

        case "BIOMETRICS":
          localStorage.setItem(
            "biometricsStatus",
            data.status ? "true" : "false",
          );
          break;

        case "NOTIFICATION_STATUS":
          localStorage.setItem(
            "APKnotificationPermission",
            data.status ? "true" : "false",
          );
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    var userAgent = window.navigator.userAgent.toLowerCase(),
      ios = /iphone|ipod|ipad/.test(userAgent);

    if (ios) {
      //ios
      window.addEventListener("message", handleMessages);
    } else {
      //not iOS
      document.addEventListener("message", handleMessages);
    }
    return () => {
      var userAgent = window.navigator.userAgent.toLowerCase(),
        ios = /iphone|ipod|ipad/.test(userAgent);

      if (ios) {
        window.removeEventListener("message", handleMessages);
      } else {
        document.removeEventListener("message", handleMessages);
      }
    };
  }, []);

  useEffect(() => {
    // only set if the value is not null
    if (localStorage.getItem("firstZoom") === null) {
      localStorage.setItem("firstZoom", "6");
    }
    if (localStorage.getItem("onlineZoom") === null) {
      localStorage.setItem("onlineZoom", "14");
    }
    if (localStorage.getItem("simulatorZoom") === null) {
      localStorage.setItem("simulatorZoom", "50");
    }
    if (localStorage.getItem("zoomOn") === null) {
      localStorage.setItem("zoomOn", "تمامی خودروها");
    }
  }, []);
  const detector = new DeviceDetector();
  const os: any = detector.parse(window.navigator.userAgent).os;
  return (
    <>
      <Helmet>
        <title>{title} GPS</title>
        {linkTags?.map((linkTag, index) => (
          <link key={index} rel={linkTag.rel} href={linkTag.href} />
        ))}
        {metaTags?.map((metaTag, index) => (
          <meta key={index} name={metaTag.name} content={metaTag.content} />
        ))}
      </Helmet>

      {!window.matchMedia("(display-mode: standalone)").matches &&
      os.name === "iOS" ? (
        <InstallModal />
      ) : (
        <CustomInstallModal />
      )}
      <DeviceRotate>
        <div className="app noselect" data-theme={theme}>
          <Notification
            show={show}
            setShow={setShow}
            notification={notification}
          />
          <ThemeProvider theme={darkTheme}>
            {viewportWidth >= 1200 ? (
              <RouterPc
                checkFcm={checkFcm}
                switchTheme={switchTheme}
                theme={theme}
              />
            ) : (
              <Router
                getFcmAPK={getFcmAPK}
                checkFcm={checkFcm}
                switchTheme={switchTheme}
                theme={theme}
              />
            )}
          </ThemeProvider>
          <ToastContainer />
        </div>
      </DeviceRotate>
    </>
  );
};

export default App;
